import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

const PERFILES_BASE = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
const PERFILES_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${PERFILES_BASE}/perfiles`,
  url: `${PERFILES_BASE}/perfiles`,
  name: 'Búsqueda de Tipsters y Perfiles de Apostadores | Apuestamente',
  description: 'Encuentra y sigue a los mejores tipsters de apuestas deportivas en España.',
  inLanguage: 'es-ES',
};

interface Profile {
  id: string;
  username: string;
  nombre: string;
  plan: string;
  avatar_url: string | null;
  roi: number;
  total_picks: number;
  won_picks: number;
  sport: string | null;
  speciality: string | null;
  is_verified: boolean;
  followers: number;
  badge: string | null;
  badge_color: string | null;
  created_at: string;
  isFollowing?: boolean;
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function Avatar({ profile, size = 'md' }: { profile: Profile; size?: 'sm' | 'md' | 'lg' }) {
  const dim = size === 'lg' ? 'w-16 h-16 text-lg' : size === 'sm' ? 'w-9 h-9 text-xs' : 'w-12 h-12 text-sm';
  return (
    <div className="relative flex-shrink-0">
      {profile.avatar_url ? (
        <img
          src={profile.avatar_url}
          alt={profile.username}
          className={`${dim} rounded-full object-cover border-2 border-[#2a2d3a]`}
        />
      ) : (
        <div className={`${dim} rounded-full bg-gradient-to-br from-[#00e87a]/30 to-[#00e87a]/10 border-2 border-[#00e87a]/20 flex items-center justify-center`}>
          <span className="text-[#00e87a] font-black uppercase">
            {(profile.username ?? 'U').slice(0, 2)}
          </span>
        </div>
      )}
      {profile.is_verified && (
        <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 flex items-center justify-center bg-[#00e87a] rounded-full border-2 border-[#151719]">
          <i className="ri-check-line text-black" style={{ fontSize: '9px' }}></i>
        </div>
      )}
    </div>
  );
}

// ── Plan badge ────────────────────────────────────────────────────────────────
function PlanBadge({ plan }: { plan: string }) {
  const cfg =
    plan === 'elite'   ? { label: 'ELITE',   cls: 'bg-[#ffd700]/15 text-[#ffd700] border-[#ffd700]/20' } :
    plan === 'premium' ? { label: 'PRO',     cls: 'bg-[#00e87a]/15 text-[#00e87a] border-[#00e87a]/20' } :
                         { label: 'FREE',    cls: 'bg-white/5 text-[#6b7280] border-[#2a2d3a]' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border tracking-wider ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ── Follow button ─────────────────────────────────────────────────────────────
function FollowButton({
  profile,
  currentUserId,
  onToggle,
}: {
  profile: Profile;
  currentUserId: string | null;
  onToggle: (id: string, following: boolean) => void;
}) {
  const [toggling, setToggling] = useState(false);
  const { openLoginModal, isRegistered } = useAuth();
  const isOwn = currentUserId === profile.id;

  if (isOwn) {
    return (
      <Link
        to="/perfil"
        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-[#1e2028] text-[#a8b2c1] border border-[#2a2d3a] hover:border-[#00e87a]/30 hover:text-[#00e87a] transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-edit-line text-xs"></i>
        Mi perfil
      </Link>
    );
  }

  const handleClick = async () => {
    if (!isRegistered) { openLoginModal(); return; }
    if (!currentUserId || toggling) return;
    setToggling(true);
    try {
      if (profile.isFollowing) {
        await supabase.from('follows').delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', profile.id);
        onToggle(profile.id, false);
      } else {
        await supabase.from('follows').insert({
          follower_id: currentUserId,
          following_id: profile.id,
        });
        onToggle(profile.id, true);
      }
    } finally {
      setToggling(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={toggling}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${
        profile.isFollowing
          ? 'bg-[#00e87a]/15 text-[#00e87a] border border-[#00e87a]/30 hover:bg-[#ef4444]/10 hover:text-[#ef4444] hover:border-[#ef4444]/30'
          : 'bg-[#00e87a] text-black border border-transparent hover:bg-[#00d070]'
      }`}
    >
      {toggling ? (
        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <i className={`${profile.isFollowing ? 'ri-user-follow-line' : 'ri-user-add-line'} text-xs`}></i>
      )}
      {profile.isFollowing ? 'Siguiendo' : 'Seguir'}
    </button>
  );
}

// ── Profile Card ──────────────────────────────────────────────────────────────
function ProfileCard({
  profile,
  currentUserId,
  onFollowToggle,
}: {
  profile: Profile;
  currentUserId: string | null;
  onFollowToggle: (id: string, following: boolean) => void;
}) {
  const hitRate = profile.total_picks > 0
    ? Math.round((profile.won_picks / profile.total_picks) * 100)
    : 0;

  const hasStats = profile.total_picks > 0;

  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl overflow-hidden hover:border-[#00e87a]/25 transition-all group">
      {/* Top strip */}
      <div className="h-1 w-full bg-gradient-to-r from-[#00e87a]/40 via-[#00e87a]/10 to-transparent"></div>

      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar profile={profile} size="md" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-white font-bold text-sm truncate">@{profile.username}</span>
                <PlanBadge plan={profile.plan} />
              </div>
              {profile.nombre && profile.nombre !== 'Usuario' && (
                <p className="text-[#6b7280] text-xs truncate">{profile.nombre}</p>
              )}
              <p className="text-[#4b5563] text-xs mt-0.5">
                {profile.sport ?? 'Multi-sport'}
                {profile.speciality ? ` · ${profile.speciality}` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {hasStats ? (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-[#0d0f14] rounded-xl p-3 text-center">
              <p className={`font-black text-base ${Number(profile.roi) >= 0 ? 'text-[#00e87a]' : 'text-[#ef4444]'}`}>
                {Number(profile.roi) >= 0 ? '+' : ''}{Number(profile.roi).toFixed(1)}%
              </p>
              <p className="text-[#4b5563] text-[10px] font-medium mt-0.5">ROI</p>
            </div>
            <div className="bg-[#0d0f14] rounded-xl p-3 text-center">
              <p className="text-white font-black text-base">{profile.total_picks}</p>
              <p className="text-[#4b5563] text-[10px] font-medium mt-0.5">Picks</p>
            </div>
            <div className="bg-[#0d0f14] rounded-xl p-3 text-center">
              <p className="text-[#ffd700] font-black text-base">{hitRate}%</p>
              <p className="text-[#4b5563] text-[10px] font-medium mt-0.5">Aciertos</p>
            </div>
          </div>
        ) : (
          <div className="bg-[#0d0f14] rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
            <i className="ri-time-line text-[#3d4149] text-sm"></i>
            <p className="text-[#3d4149] text-xs">Sin picks registrados aún</p>
          </div>
        )}

        {/* Footer: followers + follow button */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[#6b7280] text-xs">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-group-line text-xs"></i>
            </div>
            <span className="font-bold text-[#a8b2c1]">{profile.followers ?? 0}</span>
            <span>seguidores</span>
          </div>

          <FollowButton
            profile={profile}
            currentUserId={currentUserId}
            onToggle={onFollowToggle}
          />
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PerfilesPage() {
  const { openLoginModal, isRegistered, supabaseUser } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    document.title = 'Búsqueda de Tipsters y Perfiles de Apostadores | Apuestamente';
    const id = 'schema-perfiles';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(PERFILES_SCHEMA);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, nombre, plan, avatar_url, roi, total_picks, won_picks, sport, speciality, is_verified, followers, badge, badge_color, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching profiles:', error);
      setLoading(false);
      return;
    }

    if (!data || data.length === 0) {
      setProfiles([]);
      setLoading(false);
      return;
    }

    let followingIds: Set<string> = new Set();
    if (supabaseUser) {
      const { data: followData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', supabaseUser.id);
      followingIds = new Set((followData ?? []).map((f: { following_id: string }) => f.following_id));
    }

    const enriched = (data as Profile[]).map((p) => ({
      ...p,
      roi: Number(p.roi ?? 0),
      total_picks: p.total_picks ?? 0,
      won_picks: p.won_picks ?? 0,
      followers: p.followers ?? 0,
      isFollowing: followingIds.has(p.id),
    }));

    setProfiles(enriched);
    setLoading(false);
  }, [supabaseUser]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  // Realtime follows
  useEffect(() => {
    const channel = supabase
      .channel('follows-realtime-perfiles')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'follows' }, () => {
        fetchProfiles();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchProfiles]);

  const handleFollowToggle = (profileId: string, following: boolean) => {
    setProfiles((prev) =>
      prev.map((p) =>
        p.id === profileId
          ? { ...p, isFollowing: following, followers: following ? p.followers + 1 : Math.max(p.followers - 1, 0) }
          : p
      )
    );
  };

  const sports = ['all', 'Fútbol', 'Tenis', 'Baloncesto', 'Multi-sport'];

  const filtered = profiles
    .filter((p) => {
      const q = search.toLowerCase();
      const matchesSearch = !q
        || (p.username ?? '').toLowerCase().includes(q)
        || (p.nombre ?? '').toLowerCase().includes(q);
      const matchesSport = sportFilter === 'all' || p.sport === sportFilter;
      return matchesSearch && matchesSport;
    })
    .sort((a, b) => {
      if (sortBy === 'roi') return Number(b.roi) - Number(a.roi);
      if (sortBy === 'picks') return b.total_picks - a.total_picks;
      if (sortBy === 'followers') return b.followers - a.followers;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const followingCount = profiles.filter((p) => p.isFollowing).length;

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16">

        {/* Header */}
        <div className="bg-[#151719] border-b border-[#2a2d3a]">
          <div className="max-w-[1440px] mx-auto px-6 py-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-[#00e87a] text-xs font-bold tracking-widest uppercase mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
                  Comunidad
                </p>
                <h1 className="text-white text-4xl font-black uppercase">Perfiles</h1>
                <p className="text-[#a8b2c1] text-sm mt-2">
                  Todos los usuarios de Apuestamente · Sigue a tus tipsters favoritos
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-[#6b7280] text-xs">
                    {loading ? '...' : `${profiles.length} usuarios registrados`}
                  </span>
                  {isRegistered && followingCount > 0 && (
                    <span className="text-[#00e87a] text-xs font-semibold">
                      Siguiendo a {followingCount}
                    </span>
                  )}
                </div>
              </div>
              {!isRegistered && (
                <button
                  onClick={openLoginModal}
                  className="flex items-center gap-2 px-5 py-3 bg-[#00e87a] text-black text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap hover:bg-[#00d070] transition-colors"
                >
                  <i className="ri-user-add-line text-base"></i>
                  Crear perfil gratis
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">

          {/* Filters */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 mb-8">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280]">
                <i className="ri-search-line text-sm"></i>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar usuario..."
                className="w-full bg-[#151719] border border-[#2a2d3a] text-white text-sm rounded-xl pl-10 pr-9 py-2.5 placeholder-[#6b7280] focus:outline-none focus:border-[#00e87a]/40"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#6b7280] hover:text-white cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>

            {/* Sport filter */}
            <div className="flex items-center gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1">
              {sports.map((s) => (
                <button
                  key={s}
                  onClick={() => setSportFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sportFilter === s ? 'bg-[#00e87a]/15 text-[#00e87a]' : 'text-[#6b7280] hover:text-white'
                  }`}
                >
                  {s === 'all' ? 'Todos' : s}
                </button>
              ))}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#151719] border border-[#2a2d3a] text-[#a8b2c1] text-sm rounded-xl px-4 py-2.5 cursor-pointer focus:outline-none focus:border-[#00e87a]/40"
            >
              <option value="recent">Más recientes</option>
              <option value="roi">Por ROI</option>
              <option value="picks">Por Picks</option>
              <option value="followers">Por Seguidores</option>
            </select>

            {/* Following filter */}
            {isRegistered && followingCount > 0 && (
              <button
                onClick={() => setSportFilter(sportFilter === '__following' ? 'all' : '__following')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                  sportFilter === '__following'
                    ? 'bg-[#00e87a]/15 text-[#00e87a] border-[#00e87a]/30'
                    : 'bg-[#151719] text-[#6b7280] border-[#2a2d3a] hover:text-white'
                }`}
              >
                <i className="ri-user-follow-line text-xs"></i>
                Siguiendo ({followingCount})
              </button>
            )}
          </div>

          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-5 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#2a2d3a] flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-[#2a2d3a] rounded w-3/4"></div>
                      <div className="h-3 bg-[#2a2d3a] rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="h-14 bg-[#2a2d3a] rounded-xl mb-4"></div>
                  <div className="flex justify-between items-center">
                    <div className="h-3 bg-[#2a2d3a] rounded w-1/3"></div>
                    <div className="h-8 bg-[#2a2d3a] rounded-xl w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#00e87a]/10 mx-auto mb-4">
                <i className="ri-user-search-line text-[#00e87a] text-2xl"></i>
              </div>
              <p className="text-white font-bold text-lg mb-2">
                {search ? `Sin resultados para "${search}"` : 'Aún no hay perfiles registrados'}
              </p>
              <p className="text-[#6b7280] text-sm mb-5">
                {search ? 'Prueba con otro nombre de usuario.' : 'Sé el primero en registrarte.'}
              </p>
              {!isRegistered && !search && (
                <button
                  onClick={openLoginModal}
                  className="px-5 py-2.5 bg-[#00e87a] text-black text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap"
                >
                  Registrarse gratis
                </button>
              )}
            </div>
          )}

          {/* Grid */}
          {!loading && filtered.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map((profile) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    currentUserId={supabaseUser?.id ?? null}
                    onFollowToggle={handleFollowToggle}
                  />
                ))}
              </div>
              <p className="text-[#3d4149] text-xs text-center mt-8">
                {filtered.length === profiles.length
                  ? `${profiles.length} usuarios en total`
                  : `Mostrando ${filtered.length} de ${profiles.length} usuarios`}
              </p>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
